import api from 'store/actions/api';
import {
  flow,
  get,
  extend,
  isEqual,
  omit,
  omitBy,
  find,
  pick,
} from 'lodash/fp';
import { getFormValues, initialize as initializeForm } from 'redux-form';
import {
  push as pushRoute,
  replace as replaceRoute,
} from 'connected-react-router';
import { getUser } from 'components/user/selectors';
import { actions as layoutActions } from 'components/page/Layout';
import {
  formFromQuery,
  formToQuery,
  formToMeta,
  formScaffolding,
  parseAdsSearch,
  stringifyAdsQuery,
  PAGE_NAME,
  SORT_NAME,
} from 'components/searchAds';
import {
  selectCarMakeById,
  selectCarMakeValues,
} from 'store/ducks/manufacturers';
import { EXACT_SEARCH_TYPE } from 'components/searchAds/SearchLayout/constants';
import {
  sendRemovedFavoriteNotification,
  sendRemovedDislikeNotification,
} from 'components/Notification/actions';
import { getFavoritesUrl, getDislikesUrl } from 'helpers/namedUrls';
import { parseSearch } from 'helpers/location';
import { adsDuck, cmsDuck, uiDuck } from '../duck';
import {
  FAVORITES_OVERVIEW_FORM,
  FAVORITE_TYPE_LIKE,
  FAVORITE_TYPE_DISLIKE,
  FAVORITED_ON_DESC_SORT,
} from '../constants';

const getFavorites = get(['favoritesOverview', 'ads', 'result', 'items']);

const getExtraQueryParamsForPage = flow(
  get(['search']),
  parseSearch,
  pick(['dislike']),
);

const getFavoriteTypeFromUrl = flow(
  get(['search']),
  parseSearch,
  get(['dislike']),
  isEqual('true'),
  (isDislike) => (isDislike ? FAVORITE_TYPE_DISLIKE : FAVORITE_TYPE_LIKE),
);

export const applySort = () =>
  function* ({ getState }) {
    const form = flow(
      getFormValues(FAVORITES_OVERVIEW_FORM),
      omit([PAGE_NAME]),
      extend({ [PAGE_NAME]: 1 }),
    )(getState());
    const carMakeById = selectCarMakeById(getState());
    const query = flow(
      omitBy((value, key) => isEqual(formScaffolding[key], value)),
      formToQuery(carMakeById),
    )(form);

    const nextLocation = {
      search: stringifyAdsQuery(query),
      query,
    };

    yield replaceRoute(nextLocation);

    return nextLocation;
  };

export const loadPage = (page) =>
  function* ({ getState, getCurrentLocation }) {
    const form = flow(
      getFormValues(FAVORITES_OVERVIEW_FORM),
      omit([PAGE_NAME]),
      extend({ [PAGE_NAME]: page }),
    )(getState());

    const carMakeById = selectCarMakeById(getState());
    const query = flow(
      omitBy((value, key) => isEqual(formScaffolding[key], value)),
      formToQuery(carMakeById),
    )(form);
    const queryWithExtraParams = {
      ...query,
      ...getExtraQueryParamsForPage(getCurrentLocation()),
    };

    const nextLocation = {
      search: stringifyAdsQuery(queryWithExtraParams),
      query: queryWithExtraParams,
    };

    yield replaceRoute(nextLocation);

    return nextLocation;
  };

const maybeRedirectPage = () =>
  function* ({ getState, getCurrentLocation }) {
    const { currentPage, pageNumber } = get(
      ['favoritesOverview', 'ads', 'result', 'pagination'],
      getState(),
    );
    const page = Number(currentPage);
    const isPageIncorrect = page > 1 && page > pageNumber;

    if (isPageIncorrect) {
      const search = flow(
        get(['search']),
        parseAdsSearch,
        omit(['page']),
        stringifyAdsQuery,
      )(getCurrentLocation());

      yield replaceRoute({ search });
    }
  };

export const loadAds = () =>
  function* ({ getState, getCurrentLocation }) {
    const favoriteType = getFavoriteTypeFromUrl(getCurrentLocation());
    const trackingId = flow(getUser, get(['trackingId']))(getState());
    const formValues = getFormValues(FAVORITES_OVERVIEW_FORM)(getState());
    const meta = formToMeta()({
      ...formValues,
      [SORT_NAME]: FAVORITED_ON_DESC_SORT,
    });

    yield adsDuck.loadAds({
      meta,
      type: EXACT_SEARCH_TYPE,
      userSearchType: 'default',
      trackingId,
      query: {
        favoriteType,
      },
    });
  };

const notifyRemovedFavoriteType = (adId, favoriteType) =>
  function* ({ getState }) {
    const adTitle = flow(
      getFavorites,
      find({ id: adId }),
      get(['title']),
    )(getState());

    if (favoriteType === FAVORITE_TYPE_LIKE) {
      sendRemovedFavoriteNotification(adTitle);
    } else if (favoriteType === FAVORITE_TYPE_DISLIKE) {
      sendRemovedDislikeNotification(adTitle);
    }
  };

export const removeFavoriteForUnpublishedAd = (adId) =>
  function* ({ getState }) {
    const trackingId = flow(getUser, get(['trackingId']))(getState());
    const ad = flow(getFavorites, find({ id: adId }))(getState());

    if (ad.isFavorite) {
      yield api.removeAdFromFavorites(trackingId, adId);
      yield notifyRemovedFavoriteType(adId, FAVORITE_TYPE_LIKE);
    } else if (ad.isDislike) {
      yield api.removeAdFromDislikes(trackingId, adId);
      yield notifyRemovedFavoriteType(adId, FAVORITE_TYPE_DISLIKE);
    }

    yield loadAds();
    yield maybeRedirectPage();
  };

const notifyAdFavoriteRemoval = (adId, favoriteType) =>
  function* ({ getState }) {
    const adTitle = flow(
      getFavorites,
      find({ id: adId }),
      get(['title']),
    )(getState());

    if (favoriteType === FAVORITE_TYPE_LIKE) {
      return sendRemovedFavoriteNotification(adTitle);
    } else if (favoriteType === FAVORITE_TYPE_DISLIKE) {
      return sendRemovedDislikeNotification(adTitle);
    }
  };

const createAdFavoriteTypeRemoval =
  (favoriteType) =>
  (adId, favoriteAction = true) =>
    function* () {
      if (!favoriteAction) {
        yield notifyAdFavoriteRemoval(adId, favoriteType);
        yield loadAds();
        yield maybeRedirectPage();
      }
    };

export const toggleAdFavorite = createAdFavoriteTypeRemoval(FAVORITE_TYPE_LIKE);
export const toggleAdDislike = createAdFavoriteTypeRemoval(
  FAVORITE_TYPE_DISLIKE,
);

export const goToFavorites = () =>
  function* () {
    yield pushRoute(getFavoritesUrl());
  };

export const goToDislikes = () =>
  function* () {
    yield pushRoute(getDislikesUrl());
  };

export const fetch = (locals) =>
  function* ({ getState, getCurrentLocation }) {
    const currentLocation = getCurrentLocation();
    const initialForm = flow(
      get(['search']),
      parseAdsSearch,
      formFromQuery(selectCarMakeValues(getState())),
    )(currentLocation);
    const favoriteType = getFavoriteTypeFromUrl(getCurrentLocation());

    yield initializeForm(FAVORITES_OVERVIEW_FORM, initialForm);

    yield [
      layoutActions.fetch(locals),
      cmsDuck.tryLoad('favorites-overview'),
      loadAds(),
    ];

    yield uiDuck.set({ favoriteType });
    yield maybeRedirectPage();
  };
